/**
 * voice-v8 — LiveKit-Client-Wrapper.
 *
 * Tag 5: Connect-Lifecycle, Mic-Toggle, Data-Channel.
 * Tag 16: Audio-Track-Stream + AgentState-Forward (idle/listening/thinking/
 * speaking via Bot-Audio-Track-Events oder Custom-Data-Messages).
 */

import {
  ConnectionQuality,
  type LocalAudioTrack,
  ParticipantEvent,
  type Participant,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
  Room,
  RoomEvent,
  Track,
} from "livekit-client";
import { ANRUF_HOECHSTDAUER, ANRUF_VORBEI } from "./anruf-link.js";

// pre.887: Der fruehere Reconnect-Test-Schalter (?rctest) ist ENTFERNT — er
// konnte im localStorage haengenbleiben und die Session alle ~1 Min auffrischen
// ("startet jede 1,5 Min neu"). Wir loeschen den Alt-Schluessel einmalig beim
// Laden, damit kein Geraet mehr haengenbleibt.
try {
  localStorage.removeItem("voice-v8.rctest");
} catch {
  /* kein localStorage → ignorieren */
}

// Pre.444: Connection-Quality-Schutz. Vier strukturierte Quality-Levels für
// die UI + Logic. Übersetzung von LiveKit-ConnectionQuality auf eigene
// Skala — wir wollen "lost" und "unknown" separat behandeln, aber im UI
// gleich anzeigen (rot).
type QualityLevel = "excellent" | "good" | "poor" | "lost";

interface QualitySnapshot {
  /** Quality des LOKALEN Participants (uns) — der Wert der für User-UI relevant ist. */
  local: QualityLevel;
  /** Quality des REMOTE Bot-Participants — für Diagnose, NICHT für Bitrate-Anpassung. */
  remote: QualityLevel;
}

function mapQuality(q: ConnectionQuality): QualityLevel {
  switch (q) {
    case ConnectionQuality.Excellent:
      return "excellent";
    case ConnectionQuality.Good:
      return "good";
    case ConnectionQuality.Poor:
      return "poor";
    case ConnectionQuality.Lost:
      return "lost";
    default:
      return "good";
  }
}

export type VoiceState =
  | "idle"
  | "connecting"
  | "reconnecting"
  | "listening"
  | "thinking"
  | "speaking"
  | "paused-phone"
  | "error";

export interface VoiceCallbacks {
  onState?: (state: VoiceState) => void;
  onUserTranscript?: (text: string, isFinal: boolean) => void;
  onBotMessage?: (text: string, isFinal: boolean) => void;
  onError?: (err: Error) => void;
  /** Wird beim Mic-Enable mit einem MediaStreamTrack gerufen, beim Mic-Disable mit `null`. */
  onLocalAudio?: (track: MediaStreamTrack | null) => void;
  /**
   * Wird gerufen wenn der Bot anfängt zu sprechen (Track subscribed →
   * MediaStreamTrack), und mit `null` wenn der Bot fertig ist (Track
   * unsubscribed). Dient dem Visualizer um Bot-Audio-Level zu zeigen.
   */
  onBotAudio?: (track: MediaStreamTrack | null) => void;
  /** Strukturiertes Tool-Result für Card-Rendering (Tag 17). */
  onToolCard?: (card: ToolCardMessage) => void;
  /**
   * Pre.339: Live-Tool-Progress (claude_task_server u.a. lange Tools).
   * Frontend rendert daraus eine Live-System-Bubble mit elapsed time +
   * letztem stdout-Hinweis, statt 30s+ schwarzem Loch zu zeigen.
   */
  onToolProgress?: (progress: ToolProgressMessage) => void;
  /** Truth-Guard hat den letzten Bot-Reply als unehrlich markiert (Tag 22). */
  onTruthWarning?: (warn: TruthWarning) => void;
  /**
   * Pre.119: STT hat Audio erkannt, der Filter hat es aber als
   * Halluzination verworfen. Frontend rendert einen System-Hint im
   * Chat, damit der User sieht WAS gefiltert wurde.
   */
  onSttReject?: (info: SttRejectInfo) => void;
  /**
   * Pre.122: LiveKit UserState — speaking / listening / away. Frontend
   * nutzt das als Live-Status-Indikator ("ich höre dich…") OHNE Geister-
   * Bubbles aus Audio-Pegel zu erzeugen.
   */
  onUserState?: (state: "speaking" | "listening" | "away") => void;
  /** Agent meldet beim Start die aktive Modul-Liste. */
  onModulesList?: (modules: ModulesList) => void;
  /**
   * Pre.326: Reconnect-Versuch wird gleich gestartet. `attempt` ist 1-basiert,
   * `max` ist die Obergrenze nach der aufgegeben wird. Frontend zeigt einen
   * Toast "Verbindung wackelt — verbinde neu (1/3)…" damit der User sieht
   * dass das System nicht stumm hängt, sondern aktiv kämpft.
   */
  onReconnectAttempt?: (attempt: number, max: number) => void;
  /**
   * Pre.326: Reconnect war erfolgreich (entweder LiveKit's interner
   * Mikro-Reconnect oder unser scheduleReconnect-Pfad). Frontend zeigt
   * einen "wieder da"-Toast.
   */
  onReconnected?: () => void;
  /**
   * Pre.444: Live-Verbindungsqualität. Wird bei jeder Änderung gefeuert
   * (Local- und Remote-Participant separat). Frontend rendert einen
   * Balken-Indikator in der Voice-Bubble.
   */
  onConnectionQuality?: (snapshot: QualitySnapshot) => void;
  /**
   * Pre.444: Proaktive Warnung wenn lokale Quality 5s+ in "poor" steht.
   * Frontend zeigt einen einmaligen Toast pro Session.
   */
  onPoorConnection?: () => void;
  /**
   * Pre.446: Bot ist 8s+ in "speaking"/"thinking" State aber kein Audio
   * kommt durch. Wahrscheinlich iOS-Audio-Block — User soll aufs Display
   * tippen damit Audio freigegeben wird. Wird 1× pro Session gefeuert.
   */
  onAudioStuck?: () => void;
  /**
   * Pre.644: Preemptiver Sitzungs-Refresh läuft GLEICH (vor dem ~10min-
   * Provider-Cut). Anders als ein Crash/Wackel-Reconnect ist das geplant
   * und nahtlos — das Frontend zeigt nur einen dezenten Mikro-Hinweis
   * ("Sitzung aufgefrischt"), KEIN Crash-Banner. `forced` = true heißt:
   * keine Sprechpause gefunden, zur Hard-Deadline erzwungen.
   */
  onPreemptiveRefresh?: (forced: boolean) => void;
  /** Pre.769: ~1 Min vor der Auffrischung — dezenter Amber-Puls am Timer. */
  onPreemptiveWarn?: () => void;
}

interface SttRejectInfo {
  text: string;
  reason: string;
  noSpeech: number | null;
  avgLogprob: number | null;
}

interface ModulesList {
  active: string[];
  inactive: Array<{ name: string; reason: string }>;
  truthGuard: boolean;
}

export interface ToolCardMessage {
  tool: string;
  callId: string;
  isError: boolean;
  result: unknown;
  /**
   * 15.10.2026: was die Karte zeigt, vom Server aus den Feldern gebaut (server/werkzeug-anzeige.ts). Ungeprüft —
   * gelesen wird sie nur über leseAnzeige (lib/anzeige-karte.ts, anzeigeVon).
   */
  anzeige?: unknown;
}

/** Pre.339: Live-Status während ein lang laufendes Tool läuft. */
interface ToolProgressMessage {
  tool: string;
  callId?: string;
  phase: "started" | "running" | "done";
  elapsedMs?: number;
  lastLine?: string;
}

interface TruthWarning {
  reason?: string;
  correction?: string;
  latencyMs?: number;
}

export interface TokenResponse {
  token: string;
  url: string;
  room: string;
  identity: string;
}

export class VoiceClient {
  private room: Room | null = null;
  private callbacks: VoiceCallbacks;
  private state: VoiceState = "idle";
  private botSpeakingTracks = new Set<RemoteTrack>();
  /** Wenn true, werden Bot-Audio-HTMLAudioElements auf muted gesetzt
   * (Text-Mode: User tippt, will Bot nicht hören). */
  private botAudioMuted = false;
  /**
   * Pre.207 (B): Auto-Reconnect-Tracking. Wenn der User aktiv getrennt
   * (`disconnect()` aufruft), setzen wir userInitiatedDisconnect=true und
   * der RoomEvent.Disconnected-Handler tut NICHTS außer state="idle".
   * Bei jedem anderen Disconnect (Server-side, Gemini-Live-Crash, Network)
   * versucht der Handler einen Reconnect mit exponential backoff.
   */
  private userInitiatedDisconnect = false;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Pre.644: true während des GEPLANTEN preemptiven Refreshs (vor dem
   * ~10min-Cut). Der nächste scheduleReconnect-Lauf unterdrückt dann den
   * alarmierenden "Verbindung wackelt"-Toast + den "reconnected"-Chime —
   * das Frontend zeigt nur eine ruhige Pille. Gilt nur für den ersten
   * (geplanten) Versuch; schlägt der fehl, läuft alles wieder normal/laut.
   */
  private preemptiveRefreshInFlight = false;
  // Pre.973 (nahtloser Reconnect): Name des aktuellen LiveKit-Raums (kommt vom
  // Token-Endpoint). Bei einem unerwarteten kurzen Drop (PWA-Hintergrund/Netz)
  // fordern wir denselben Raum erneut an, damit wir wieder in der überlebenden
  // Agent-Session landen (Agent läuft dort dank closeOnDisconnect=false weiter).
  private lastRoomName: string | null = null;
  // Pre.973: true = der nächste connect() bekommt einen FRISCHEN Raum (neue
  // Session). Gesetzt von triggerForceReconnect (Watchdog/Mic/Visibility) und
  // damit auch vom preemptiven 9-Min-Refresh — die wollen alle einen frischen
  // Agent, nicht das Wiederanknüpfen an die alte (evtl. festsitzende) Session.
  private forceFreshRoomNextConnect = false;
  // Pre.443: 3 → 5 Versuche. User-Bug: bei session-close muss User
  // manuell Stop+neu-anrufen. Mehr Auto-Retries verhindern das.
  private static readonly MAX_RECONNECT_ATTEMPTS = 5;
  /**
   * Pre.337 (Strategie B): Keep-Alive-Ping gegen Provider-Idle-Drops.
   * Gemini Live / OpenAI Realtime droppen Sessions wenn längere Zeit
   * keine Aktivität war. Alle 25 Sekunden senden wir ein leichtes
   * `voice-v8.ping`-Datenpaket an den Agent → Provider sieht "Aktive
   * Session" → kein Idle-Cut.
   */
  private keepAliveTimer: ReturnType<typeof setInterval> | null = null;
  private static readonly KEEP_ALIVE_INTERVAL_MS = 25_000;
  /**
   * Pre.337 (Stummheits-Detector): Wenn der Bot 60s lang gar nichts mehr
   * sendet (kein Audio, kein Transcript, kein Data-Packet), obwohl die
   * LiveKit-Room offen ist, ist die Provider-Session vermutlich serverseitig
   * gestorben aber LiveKit weiß es nicht. Force-Disconnect + scheduleReconnect.
   */
  private lastBotActivityAt = 0;
  /**
   * Pre.450: Timestamp des letzten finalen User-Transcripts. Wird gesetzt
   * wenn der User aufhört zu reden und das STT eine fertige Final-Message
   * liefert. Der Silence-Watcher nutzt das um den Gemini-Live-Plugin-
   * Stream-Bug zu erkennen: wenn nach User-Final 20s keine Bot-Aktivität
   * kommt, ist die Plugin-Session tot → force-reconnect.
   */
  private lastUserFinalAt = 0;
  // Pre.487: 12s → 18s. Agent macht jetzt nach 5s Plugin-Bypass mit
  // session.say() — siehe agent/index.ts FunctionToolsExecuted-Handler.
  // Erst wenn auch DAS scheitert (z.B. Session ganz tot, nicht nur
  // Plugin-Bug), soll der Frontend-Reconnect greifen. 18s = 5s Bypass-
  // Frist + 8s Audio-Render + 5s Sicherheit.
  // Pre.841: 18s → 40s. Die 18s stammen aus der Pong-Ära (jedes Paket
  // zählte als Bot-Aktivität, der Wächter feuerte fast nie). Seit P1.1
  // (Pong-Whitelist) feuerte er in JEDEM aktiven Gespräch im ~20s-Takt:
  // Gemini liefert Transkripte in ~30s-PAKETEN — das User-Final kommt
  // oft NACH der längst gespielten Bot-Antwort an und schärfte den
  // Trigger fälschlich (Tickets #124/#125/#127, 2026-06-12).
  private static readonly USER_WAITING_FOR_BOT_MS = 40_000;
  // #124/#125: Gemeinsame Bremse für ALLE Watchdog-Reconnects — höchstens
  // einer pro 90s. Reconnects reparieren kein schlechtes Funknetz, sie
  // erzeugten den 20s-Abriss-Zyklus erst (Tickets #124/#125/#127).
  private lastWatchdogReconnectAt = 0;
  private static readonly WATCHDOG_RECONNECT_COOLDOWN_MS = 90_000;
  private silenceWatcherTimer: ReturnType<typeof setInterval> | null = null;
  private static readonly SILENCE_WATCHER_MS = 5_000; // Check alle 5s (pre.446: war 10s)

  /**
   * Pre.485 — Voice-Stabilität gegen 10min-Drift.
   *
   * (A) Mic-Bytes-Watchdog: Wir lesen alle 15s die WebRTC-getStats() und
   * tracken `bytesSent` des LocalAudioTracks. Wenn der LocalParticipant
   * 30s+ keine Bytes mehr publiziert UND State === "listening" → der Mic-
   * Track ist tot (iOS hat ihn pausiert, MediaStream verloren, Permission
   * widerrufen). Force-Reconnect.
   *
   * (B) Visibility-Resume: Bei document.visibility tab→hidden merken wir
   * uns den Timestamp. Beim Wechsel zurück auf visible, wenn >2min hidden
   * war → force-reconnect statt zu hoffen dass WebRTC den Hiccup überlebt.
   *
   * (C) Preemptiver Refresh in ZWEI Stufen (pre.644 — Ticket #29 sauber
   *     statt Pflaster): Geminis Live-Session reißt nach ~10min hart ab.
   *     Statt mitten im Satz abzuschneiden frischen wir VORHER auf:
   *       - SOFT (8min/480s): ab hier beim ERSTEN Sprech-Pause-Moment
   *         (Bot hört, User redet gerade nicht) nahtlos neu aufbauen.
   *       - HARD (9min/540s): findet sich keine Pause, erzwingen wir es —
   *         noch ~60s vor dem Provider-Cut (die bewährte alte Schwelle),
   *         der Server-Flush (pre.636) rettet den letzten Zwischen-Transkript.
   *         So geht keine Einsprache mehr verloren, statt sie nur
   *         nachträglich zu retten.
   */
  private sessionStartedAt = 0;
  private preemptiveWarned = false; // Pre.769: Amber-Puls-Hinweis schon gefeuert?
  // pre.888: „LANGE Gespräche"-Modell (#compress) → keine vorsorgliche
  // 9-Min-Auffrischung; die Kontext-Kompression hält die Session lange.
  private longSessionMode = false;
  private lastLocalBytesSent = 0;
  private lastLocalBytesChangedAt = 0;
  private hiddenSinceTs = 0;
  private static readonly MIC_BYTES_CHECK_INTERVAL_MS = 15_000;
  private static readonly MIC_BYTES_STALE_THRESHOLD_MS = 30_000;
  // #176: war 2*60_000 (120s) → Telefonanrufe (30s–5min) fielen durch die
  // Schwelle, Audio blieb tot, User musste manuell neu verbinden. 30s fängt
  // echte Anrufe, toleriert aber kurzes App-Wechseln (<30s = kein Reconnect).
  private static readonly VISIBILITY_HIDDEN_RECONNECT_MS = 30_000;
  // Pre.644: Zwei-Stufen-Refresh (siehe (C) oben). SOFT = ab hier auf
  // Sprech-Pause warten, HARD = zur Not erzwingen. HARD bleibt bewusst bei
  // 540s (= die bewährte alte Schwelle, ~60s vor Geminis ~10min-Cut) —
  // wir schieben die Deadline NICHT nach hinten, sondern beginnen nur
  // FRÜHER (480s) graceful auf eine Sprechpause zu warten.
  private static readonly PREEMPTIVE_SOFT_AGE_MS = 480_000; // 8 min
  private static readonly PREEMPTIVE_HARD_AGE_MS = 540_000; // 9 min
  // Pre.769: absoluter letzter Ausweg. Bis HARD warten wir auf eine Pause /
  // dass der Bot seinen Satz beendet (kein Schnitt mitten im Satz); erst ab
  // VERY_HARD (kurz vor Geminis ~10min-Cut) wird auch im Satz erzwungen.
  private static readonly PREEMPTIVE_VERYHARD_AGE_MS = 585_000; // 9:45 min
  /** Pre.644: letzter bekannter User-Sprech-Status (aus voice-v8.user_state). */
  private userSpeaking = false;
  private micWatchdogTimer: ReturnType<typeof setInterval> | null = null;
  private onVisibilityChange: (() => void) | null = null;
  // Pre.446: ZWEI Schwellen statt einer. Im listening-State (User redet, Bot
  // hört) ist 60s OK. Im speaking/thinking-State (Bot sollte gerade
  // antworten) ist 25s schon zu viel — User wartet sonst minutenlang.
  private static readonly SILENCE_THRESHOLD_LISTENING_MS = 60_000;
  private static readonly SILENCE_THRESHOLD_BOT_ACTIVE_MS = 25_000;
  // Pre.446: User-sichtbare Warnung wenn Bot 8s+ "speaking" oder "thinking"
  // ohne Audio-Activity — Hinweis dass evtl. iOS-Audio blockiert.
  // Pre.459: SILENCE_USER_HINT_MS + silenceHintShown entfernt — User-
  // Banner "Tippe Bildschirm" war Fehlalarm bei Gemini-Realtime-Greeting.

  // Pre.444: Connection-Quality-State. Bei jeder Quality-Änderung halten
  // wir Snapshot + Eintritts-Zeitstempel — daraus leiten wir Adaptive-
  // Bitrate, Poor-Warning und Preemptive-Reconnect ab.
  private quality: QualitySnapshot = { local: "good", remote: "good" };
  private localQualityEnteredAt = Date.now();
  private poorWarningSent = false;
  private static readonly POOR_WARN_MS = 5_000;
  private static readonly PREEMPTIVE_RECONNECT_MS = 15_000;
  private currentBitrateBucket: "high" | "low" = "high";
  private bitrateChangeTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly BITRATE_DEBOUNCE_MS = 3_000;
  private static readonly BITRATE_HIGH_KBPS = 24_000;
  private static readonly BITRATE_LOW_KBPS = 12_000;
  /**
   * Pre.217: Bot-Transcription-Akkumulator. Realtime-Modi (Gemini Live,
   * OpenAI Realtime) emittieren oft MEHRERE TranscriptionSegments pro
   * Bot-Reply (Satz für Satz, jeder mit eigener segmentId und seg.final=
   * true). Wenn wir jedes Segment direkt als isFinal an onBotMessage
   * forwarden, schließt das Frontend liveBotBubble nach jedem Satz und
   * öffnet eine neue → Doppel-/Triple-Bubble pro Reply (Live-Bug Martin
   * 23:11: "Es scheint technische" / "Es scheint" / "zu geben…" als drei
   * Bubbles).
   *
   * Fix: Akkumulator hält pro segmentId den letzten text. Streaming-
   * Updates (isFinal=false, Frontend nutzt fillBubbleWithText) feuern
   * mit dem JOIN aller Segmente. Ein 1.5s Debounce-Timer markiert die
   * Reply als final → onBotMessage(fullText, true) → pushHistory einmal
   * pro Reply.
   */
  private botSegments = new Map<string, string>();
  private botFinalTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly BOT_REPLY_FINAL_DEBOUNCE_MS = 1500;
  // #131 (Ghost-Messages): Text + Zeitpunkt der zuletzt FINALISIERTEN Reply.
  // Gemini Live schickt manchmal ein verspätetes Segment EINER bereits
  // abgeschlossenen Reply (>1.5s nach dem Debounce → botSegments ist schon
  // geleert). Ohne Gedächtnis startet das eine FRISCHE Bubble mit einem
  // Textfragment der alten Antwort = Geister-Doppelung. Im Grace-Fenster
  // verwerfen wir solche Fragmente, die NICHTS Neues über die letzte Reply
  // hinaus enthalten.
  private lastFinalText = "";
  private lastFinalAt = 0;
  private static readonly BOT_LATE_ECHO_GRACE_MS = 4000;

  constructor(callbacks: VoiceCallbacks = {}) {
    this.callbacks = callbacks;
  }

  private setState(state: VoiceState): void {
    if (state === this.state) return;
    this.state = state;
    this.callbacks.onState?.(state);
  }

  private connectInFlight = false;
  private noSenderTicks = 0;
  private micBlockedReconnects = 0;
  // P1.1 (Audit): Zeitstempel der letzten ECHTEN User-Sprache (≥1s am
  // Stück, gemeldet von app.ts/setMicLevel) — Churn-Bremse für den
  // SilenceWatcher: einvernehmliche Stille ist kein toter Bot.
  private lastLocalSpeechAt = 0;
  // P2.2 (Audit): Mute ist ein MIKRO-Zustand, kein Call-Zustand — das
  // frühere setState("idle") beim Muten killte Banner+Timer und trieb
  // den Focus-Mode in einen Selbst-Unmute-Loop.
  private micMuted = false;
  // pre.910 (Audit P0-1/2): GEWÜNSCHTER Mikro-Zustand des Users. connectInner()
  // ist der gemeinsame Pfad für Erst-Connect UND jeden Reconnect (alle ~9 Min)
  // und schaltete das Mikro bisher bedingungslos wieder ein → Stumm-Bug
  // („obwohl ich auf stumm geschalten habe"). Dieser Wunsch überlebt Reconnects
  // und wird in connectInner reappliziert. Single Source of Truth für Mute.
  private desiredMicEnabled = true;

  /** Ticket 2728: Kennung eines Anrufs von Vespera fuer den NAECHSTEN Token-Abruf. */
  private anrufId: string | null = null;
  /**
   * Die Kennung des Anrufs von Vespera, der gerade LAEUFT (nach der Annahme).
   * Wird die App mitten darin neu eingewaehlt (Auffrischung nach 8-9 Min, frischer
   * Raum), geht sie als `fortsetzung` mit — der Server haelt die Hoechstdauer
   * dann weiter. Weg beim Auflegen und beim Aufgeben.
   */
  private laufenderAnruf: string | null = null;
  setzeAnrufId(id: string | null): void {
    this.anrufId = id;
  }

  async connect(): Promise<void> {
    // Idempotent: wenn schon verbunden ODER gerade am Verbinden → no-op.
    // Pre.837 (Audit P1.7): connectInFlight deckt zusätzlich das
    // RECONNECT-Fenster ab (state="reconnecting", room erst spät gesetzt)
    // — das Log zeigte ZWEI parallele Agent-Sessions, beide führten Tools
    // aus. P1.2: userInitiatedDisconnect hier IMMER zurücksetzen — das
    // Flag leckte und verschluckte den nächsten echten Drop als „User
    // hat aufgelegt".
    if (this.room || this.state === "connecting" || this.connectInFlight) {
      return;
    }
    this.connectInFlight = true;
    this.userInitiatedDisconnect = false;
    try {
      await this.connectInner();
    } finally {
      this.connectInFlight = false;
    }
  }

  private async connectInner(): Promise<void> {
    // Pre.326: beim Reconnect-Pfad bleibt der State auf "reconnecting"
    // (amber pulse) — nicht zurück auf "connecting" (blau pulse) springen,
    // sonst sieht der User aus "Verbindung wackelt" wird "neuer Anruf".
    if (this.state !== "reconnecting") {
      this.setState("connecting");
    }

    const settings = (() => {
      try {
        const raw = localStorage.getItem("voice-v8.settings");
        return raw ? JSON.parse(raw) : undefined;
      } catch {
        return undefined;
      }
    })();
    // pre.888/972: Die „LANGE Gespräche"-Option (#compress / 2.5-native-audio)
    // wurde entfernt — der 9-Min-Fix läuft über den Plugin-Reconnect, nicht über
    // Kompression. longSessionMode bleibt damit immer false (vorsorgliche
    // Auffrischung normal aktiv).
    this.longSessionMode = false;
    // Pre.973 (nahtloser Reconnect): Bei einem AUTO-Reconnect nach einem
    // unerwarteten Drop (Hintergrund/Netz — reconnectAttempts>=1, KEIN Force-/
    // Preemptive-Reconnect) fordern wir DENSELBEN Raum erneut an. Der Agent ist
    // dort dank closeOnDisconnect=false noch in seiner Karenz → wir knüpfen an
    // dieselbe Live-Session an statt eine neue zu starten. Frische Anrufe
    // (reconnectAttempts==0) und alle Force-/Preemptive-Pfade bekommen einen
    // frischen Raum. Sicherheitsnetz: landen wir doch in einem leeren Raum
    // (Agent-Karenz abgelaufen), feuert der Silence-Watchdog → Force-Reconnect
    // mit frischem Raum (= heutiges Verhalten). Schlimmstenfalls wie bisher.
    const reuseRoom =
      !this.forceFreshRoomNextConnect &&
      this.lastRoomName !== null &&
      this.reconnectAttempts >= 1
        ? this.lastRoomName
        : null;
    this.forceFreshRoomNextConnect = false;
    if (reuseRoom) {
      console.log(`[webrtc] reconnect → reusing room ${reuseRoom}`);
    }
    // P2.1 (Audit): Token-Fetch mit 10s-Deckel — ein hängender Fetch war
    // ein ewiges "verbinde…" mit totem Auflegen-Knopf.
    const tokenCtl = new AbortController();
    const tokenTimer = setTimeout(() => tokenCtl.abort(), 10_000);
    let tokenRes: Response;
    let tokenAnruf = false;
    let callIdFuerLauf: string | null = null;
    try {
      // Ticket 2728: die Kennung eines Anrufs von Vespera gilt fuer GENAU
      // diesen einen Abruf (der Server nimmt sie einmalig an).
      const callId = this.anrufId;
      this.anrufId = null;
      tokenRes = await fetch("/api/token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          settings,
          ...(reuseRoom ? { room: reuseRoom } : {}),
          ...(callId ? { callId } : {}),
          ...(!callId && !reuseRoom && this.laufenderAnruf
            ? { fortsetzung: this.laufenderAnruf }
            : {}),
        }),
        signal: tokenCtl.signal,
      });
      tokenAnruf = callId !== null;
      callIdFuerLauf = callId;
    } finally {
      clearTimeout(tokenTimer);
    }
    if (!tokenRes.ok) {
      if (tokenAnruf && [404, 409, 410].includes(tokenRes.status)) {
        // Der Anruf ist vorbei, der Client nicht kaputt: zurueck auf idle, sonst
        // hielte "connecting" den naechsten Anruf-Knopf fuer schon gedrueckt.
        this.setState("idle");
        throw new Error(ANRUF_VORBEI);
      }
      // Die Hoechstdauer hat den Anruf beendet: kein endloses Neu-Einwaehlen.
      if (tokenRes.status === 410 || tokenRes.status === 429) {
        const code = (
          (await tokenRes.json().catch(() => null)) as { error?: string } | null
        )?.error;
        if (code === "anruf-hoechstdauer" || code === "anruf-beendet") {
          this.laufenderAnruf = null;
          throw new Error(ANRUF_HOECHSTDAUER);
        }
      }
      throw new Error(`token endpoint failed: ${tokenRes.status}`);
    }
    const {
      token,
      url,
      room: assignedRoom,
    } = (await tokenRes.json()) as TokenResponse;
    if (tokenAnruf) this.laufenderAnruf = callIdFuerLauf;
    // Pre.973: Raumname für einen evtl. nahtlosen Reconnect merken.
    if (assignedRoom) this.lastRoomName = assignedRoom;

    const room = new Room({ adaptiveStream: true, dynacast: true });

    room
      .on(RoomEvent.Disconnected, () => {
        this.callbacks.onLocalAudio?.(null);
        this.room = null;
        // Pre.337: Keep-Alive + Stummheits-Watcher abreißen — beide
        // brauchen einen lebenden Room. Werden bei nächstem connect()
        // neu gestartet.
        this.stopKeepAlive();
        this.stopSilenceWatcher();
        // Pre.485: Mic-Watchdog + Visibility-Handler aufräumen
        this.stopMicWatchdog();
        this.detachVisibilityHandler();
        // Pre.444: Quality-State zurücksetzen — sonst tragen wir alte
        // "poor"-Timestamps in den nächsten Connect.
        if (this.bitrateChangeTimer) {
          clearTimeout(this.bitrateChangeTimer);
          this.bitrateChangeTimer = null;
        }
        this.quality = { local: "good", remote: "good" };
        this.localQualityEnteredAt = Date.now();
        this.poorWarningSent = false;
        this.currentBitrateBucket = "high";
        // Pre.207 (B): Auto-Reconnect bei unerwartetem Disconnect.
        // Nur wenn user NICHT aktiv aufgelegt hat (siehe disconnect()).
        if (!this.userInitiatedDisconnect) {
          this.scheduleReconnect();
        } else {
          this.userInitiatedDisconnect = false;
          this.reconnectAttempts = 0;
          // Pre.973: bewusstes Auflegen → den gemerkten Raum verwerfen, damit
          // der nächste Anruf garantiert frisch startet (kein Wiederanknüpfen
          // an ein altes Gespräch).
          this.lastRoomName = null;
          this.laufenderAnruf = null;
          this.setState("idle");
        }
      })
      // Pre.326: LiveKit's interner Mikro-Reconnect (Network-Hiccup,
      // WiFi-Switch). Der Room überlebt und kommt zurück, aber während
      // dieser Phase ist KEIN Audio durchsetzbar. Ohne dieses Mapping
      // bleibt das Telefon-Icon grün während der User ins Leere spricht —
      // genau das Phänomen "sucht etwas und Verbindung bricht ab, obwohl
      // Telefonzeichen immer noch ist".
      .on(RoomEvent.Reconnecting, () => {
        this.setState("reconnecting");
      })
      .on(RoomEvent.Reconnected, () => {
        // Mikro-Reconnect war erfolgreich — zurück in den "wir hören dich"-
        // State. Ein Toast vom Frontend informiert den User.
        this.callbacks.onReconnected?.();
        this.setState("listening");
      })
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        if (track.kind === Track.Kind.Audio) {
          const el = track.attach();
          el.hidden = true;
          el.muted = this.botAudioMuted;
          // Pre.446: iOS-Safari Standalone-PWA blockt das autoplay vom
          // track.attach() manchmal. Wir setzen playsinline + rufen
          // explicit play() mit Catch.
          // Pre.458: KEIN onAudioStuck mehr beim play()-Catch! iOS resolved
          // den play() oft 1s später trotzdem korrekt, aber das Modal
          // bleibt sichtbar und verwirrt den User. Stattdessen: silent
          // skip — der silence-watcher (8s+ stumm) triggert dann wirklich
          // wenn nichts kommt.
          el.setAttribute("playsinline", "true");
          document.body.appendChild(el);
          void el.play().catch((err) => {
            console.warn(
              "[webrtc] bot-audio play() blocked (will retry on next user-gesture):",
              err,
            );
            // Pre.478: Wenn iOS play() ablehnt — auf nächste User-Gesture
            // warten (Tap irgendwo auf Document) und dann nochmal versuchen.
            // Vorher: silent skip → User hörte nichts und musste neu connecten.
            const retry = (): void => {
              void el.play().catch(() => {
                /* immer noch nicht — geben wir auf */
              });
              document.removeEventListener("pointerdown", retry, true);
              document.removeEventListener("touchstart", retry, true);
              document.removeEventListener("click", retry, true);
            };
            document.addEventListener("pointerdown", retry, {
              capture: true,
              once: true,
            });
            document.addEventListener("touchstart", retry, {
              capture: true,
              once: true,
            });
            document.addEventListener("click", retry, {
              capture: true,
              once: true,
            });
          });
          // Bot starts a new audio publication when it speaks.
          this.botSpeakingTracks.add(track);
          this.markBotActivity();
          this.setState("speaking");
          this.callbacks.onBotAudio?.(track.mediaStreamTrack);
        }
      })
      .on(
        RoomEvent.TrackUnsubscribed,
        (track: RemoteTrack, _pub: RemoteTrackPublication) => {
          track.detach().forEach((el) => el.remove());
          this.botSpeakingTracks.delete(track);
          if (this.botSpeakingTracks.size === 0) {
            this.setState("listening");
            this.callbacks.onBotAudio?.(null);
          }
        },
      )
      .on(RoomEvent.DataReceived, (payload: Uint8Array) => {
        // P1.1 (Audit): KEIN pauschales markBotActivity mehr — sonst
        // hält jedes interne Paket (Pong alle 25s, Telemetrie) den
        // SilenceWatcher blind und Zombie-Sessions leben ewig. Echte
        // Bot-Aktivität erkennt handleDataPacket per Typ-Whitelist.
        this.handleDataPacket(payload);
      })
      // Pre.444: Verbindungsqualität. LiveKit feuert das pro Participant
      // bei jedem Wechsel zwischen excellent/good/poor/lost. Wir tracken
      // Local + Remote separat, leiten daraus Adaptive-Bitrate + Warning +
      // Preemptive-Reconnect ab.
      .on(
        RoomEvent.ConnectionQualityChanged,
        (q: ConnectionQuality, participant?: Participant) => {
          this.handleQualityChange(mapQuality(q), participant, room);
        },
      )
      .on(
        RoomEvent.TranscriptionReceived,
        (segments, participant?: Participant) => {
          for (const seg of segments) {
            const isLocal =
              participant?.identity === room.localParticipant.identity;
            if (isLocal) {
              this.callbacks.onUserTranscript?.(seg.text, seg.final);
              if (seg.final && seg.text && seg.text.trim().length > 0) {
                // Pre.450: User hat einen kompletten Satz gesagt. Wenn
                // jetzt 20s+ keine Bot-Aktivität kommt → Plugin tot.
                this.lastUserFinalAt = Date.now();
              }
            } else {
              this.markBotActivity();
              this.handleBotSegment(seg.id, seg.text);
            }
          }
        },
      );

    // P2.1 (Audit): room.connect kann ewig hängen. 15s-Deckel; im
    // Timeout-Fall den evtl. später doch noch verbindenden Room AKTIV
    // trennen — sonst spielt ein untracked Geister-Room Audio ins Leere.
    let connectTimer: ReturnType<typeof setTimeout> | null = null;
    try {
      await Promise.race([
        room.connect(url, token),
        new Promise<never>((_, reject) => {
          connectTimer = setTimeout(() => {
            void room.disconnect().catch(() => undefined);
            reject(new Error("Verbindungsaufbau-Timeout (15s)"));
          }, 15_000);
        }),
      ]);
    } finally {
      if (connectTimer) clearTimeout(connectTimer);
    }
    // P2.1: Hat der User WÄHREND des Aufbaus aufgelegt → nicht wieder
    // auferstehen, Room sauber trennen, leise abbrechen.
    if (this.userInitiatedDisconnect) {
      void room.disconnect().catch(() => undefined);
      throw new Error("aborted");
    }
    this.room = room;

    try {
      await room.localParticipant.setMicrophoneEnabled(true);
    } catch (micErr) {
      // Pre.834: iOS-Mikrofon-SPERRE (User hat einmal „Nicht erlauben"
      // getippt) — vorher blieb der Call als Zombie „verbunden" stehen,
      // ohne dass der Bot je etwas hörte. Jetzt: sauber trennen + klarer
      // deutscher Fehler mit Anleitung (app.ts mappt MIC_BLOCKED).
      try {
        await room.disconnect();
      } catch {
        /* egal */
      }
      const msg = micErr instanceof Error ? micErr.message : String(micErr);
      if (/not allowed|denied|NotAllowed/i.test(msg)) {
        throw new Error("MIC_BLOCKED");
      }
      throw micErr;
    }
    // P2.1: zweites Abbruch-Fenster (der iOS-Permission-Prompt kann lange
    // stehen — Auflegen währenddessen muss greifen).
    if (this.userInitiatedDisconnect) {
      this.room = null;
      void room.disconnect().catch(() => undefined);
      throw new Error("aborted");
    }
    this.noSenderTicks = 0;
    this.micBlockedReconnects = 0;
    // pre.910 (Audit P0-1/2): User-Mute über (Re-)Connects hinweg respektieren.
    // connectInner läuft bei JEDEM Reconnect (alle ~9 Min). Vorher ging das Mikro
    // dabei stumm-geschaltet wieder live ("obwohl ich auf stumm geschalten habe").
    if (!this.desiredMicEnabled) {
      await room.localParticipant
        .setMicrophoneEnabled(false)
        .catch(() => undefined);
    }
    this.micMuted = !this.desiredMicEnabled;
    this.forwardLocalMicTrack();

    // Pre.445: iOS-Safari-Autoplay-Unblock. User-Bug 2026-05-22: Greeting-
    // MP3 kommt am Phone gar nicht — vermutet wegen Safari-Autoplay-Policy.
    // Der setMicrophoneEnabled-Call lief in der User-Gesture-Klammer
    // (Anruf-Button), aber das spätere `new Audio().play()` (400ms später
    // beim greeting-tts) ist nicht mehr drin und wird geblockt.
    // Fix: hier — noch in derselben Gesture — ein silent-MP3 priemen.
    // Danach darf der Browser im Tab Audio.play() ohne Gesture rufen.
    void this.primeAudioPlayback();
    room.localParticipant.on(ParticipantEvent.LocalTrackPublished, () =>
      this.forwardLocalMicTrack(),
    );
    room.localParticipant.on(ParticipantEvent.LocalTrackUnpublished, () =>
      this.forwardLocalMicTrack(),
    );

    // Pre.337: Keep-Alive + Stummheits-Detector starten sobald die Room
    // steht. Beide stoppen sich gegenseitig sauber bei disconnect() oder
    // Reconnect-Versuchen.
    this.startKeepAlive();
    this.startSilenceWatcher();
    // Pre.485: Drei zusätzliche Stabilitäts-Layer
    this.sessionStartedAt = Date.now();
    this.preemptiveWarned = false; // Pre.769: Puls-Hinweis pro Session neu
    this.lastLocalBytesSent = 0;
    this.lastLocalBytesChangedAt = Date.now();
    this.startMicWatchdog();
    this.attachVisibilityHandler();

    this.setState("listening");
  }

  /**
   * Pre.337 (Strategie B): Sendet alle KEEP_ALIVE_INTERVAL_MS ein
   * `voice-v8.ping`-Data-Packet an den Agent. Verhindert dass Realtime-
   * Provider (Gemini Live, OpenAI Realtime) die Session als idle einstufen
   * und droppen. Kostet ~1 Packet pro 25s — vernachlässigbar.
   */
  private startKeepAlive(): void {
    this.stopKeepAlive();
    this.keepAliveTimer = setInterval(() => {
      if (!this.room) return;
      try {
        const payload = JSON.stringify({
          type: "voice-v8.ping",
          ts: Date.now(),
        });
        void this.room.localParticipant.publishData(
          new TextEncoder().encode(payload),
          { reliable: false, topic: "voice-v8" },
        );
      } catch {
        /* ignore — wenn der Send fehlschlägt, wird der nächste Tick es
         * eh wieder versuchen. */
      }
    }, VoiceClient.KEEP_ALIVE_INTERVAL_MS);
  }

  private stopKeepAlive(): void {
    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
  }

  /**
   * Ticket #124: Gemeinsames Gate für ALLE Watchdog-getriebenen Force-
   * Reconnects (Silence-Watcher, Preemptive). True = darf feuern (und
   * setzt den Cooldown-Stempel). False = unterdrückt, weil der letzte
   * Watchdog-Reconnect < 90s her ist — verhindert den Abriss-Zyklus.
   */
  private watchdogReconnectAllowed(label: string): boolean {
    const since = Date.now() - this.lastWatchdogReconnectAt;
    if (since < VoiceClient.WATCHDOG_RECONNECT_COOLDOWN_MS) {
      console.warn(
        `[webrtc] ${label}: Watchdog-Reconnect unterdrückt (Cooldown, letzter vor ${Math.round(since / 1000)}s)`,
      );
      return false;
    }
    this.lastWatchdogReconnectAt = Date.now();
    return true;
  }

  /**
   * Pre.337 (Stummheits-Detector): Wenn die Provider-Session serverseitig
   * stirbt ABER LiveKit's Room offen bleibt (häufig bei Gemini-Live-Crashes
   * — WebRTC-Tunnel lebt, Bot-Audio kommt aber nicht mehr), kommt KEIN
   * RoomEvent.Disconnected. Frontend hätte keinen Anlass zum Reconnect.
   *
   * Lösung: alle 10s prüfen ob in den letzten 75s ÜBERHAUPT etwas vom
   * Bot kam (Audio/Transcript/Data). Wenn nein UND der State sagt
   * "listening" (also wir warten auf den Bot) → forced disconnect +
   * scheduleReconnect übernimmt.
   *
   * 75s ist großzügig — typische Bot-Stille nach einer Frage ist 1-3s,
   * längste reguläre Wartezeit bei langem Tool-Call wäre ~20-30s.
   */
  private startSilenceWatcher(): void {
    this.stopSilenceWatcher();
    this.lastBotActivityAt = Date.now();
    this.lastUserFinalAt = 0;
    this.silenceWatcherTimer = setInterval(() => {
      if (!this.room) return;
      // P2.2 (Audit): Im Mute ist Stille gesund — keine Zombie-Wertung.
      if (this.micMuted) {
        this.lastBotActivityAt = Date.now();
        return;
      }
      // Pre.841: Solange Bot-Audio-Tracks abonniert sind, LEBT die Session
      // (Teardown feuert TrackUnsubscribed/Disconnected). Ohne diesen
      // Refresh kappte die 25s-botActive-Schwelle lange Bot-Antworten
      // mitten im Satz — markBotActivity feuerte nur EINMAL beim
      // Track-Subscribe, nicht während des Abspielens.
      if (this.botSpeakingTracks.size > 0) {
        this.lastBotActivityAt = Date.now();
      }
      const idleMs = Date.now() - this.lastBotActivityAt;
      const botActive = this.state === "speaking" || this.state === "thinking";

      // Pre.459: onAudioStuck-Trigger komplett entfernt. Pre.446 hatte
      // ihn als User-Hilfe gemeint, aber bei Gemini-Realtime triggert er
      // FALSCH beim ersten Greeting (Plugin schickt kein Audio für's
      // automatische Greeting). User sah Banner "Tippe Bildschirm",
      // musste aber gar nicht tippen weil normale Bot-Antworten danach
      // ganz normal hörbar sind. Verwirrend statt hilfreich.
      // Bei ECHTEM Bot-stumm-Zustand fährt der unten kommende
      // force-reconnect-Pfad → user sieht "Verbindung wackelt" Banner.
      void botActive; // silenceHintShown stays unused now

      // Pre.450: Gemini-Live-Plugin-Stream-Bug-Detection.
      // Wenn der User einen Final-Satz gesagt hat aber innerhalb von 20s
      // KEINE Bot-Activity kommt UND der State immer noch listening ist
      // (Bot ist nicht mal in "thinking" gegangen) → Plugin-Session
      // korrupt nach Tool-Call. Force-reconnect, weil 60s warten unzumutbar.
      const waitingForBot =
        this.lastUserFinalAt > 0 &&
        Date.now() - this.lastUserFinalAt >=
          VoiceClient.USER_WAITING_FOR_BOT_MS &&
        this.state === "listening" &&
        this.lastBotActivityAt < this.lastUserFinalAt &&
        // Pre.841: Das Transkript-Final ist wegen Gemini-Paket-Latenz KEIN
        // verlässlicher Zeitanker — zusätzlich muss der User seit der
        // letzten Bot-Aktivität auch LOKAL gesprochen haben (Mikro-Pegel,
        // immun gegen Transkript-Verspätung). Sonst schärfte ein spät
        // angekommenes Final den Trigger nach längst gespielter Antwort.
        this.lastLocalSpeechAt > this.lastBotActivityAt;
      if (waitingForBot) {
        // #125: Bei schlechtem LOKALEN Netz ist die fehlende Bot-Antwort fast
        // immer das Netz, NICHT das Plugin — ein Force-Reconnect macht es nur
        // schlimmer (3-5s Neuaufbau, der gleich wieder abreißt → 20s-Schleife).
        // Echte Netz-Abbrüche fängt LiveKits Disconnected-Event von selbst.
        if (this.quality.local === "poor" || this.quality.local === "lost") {
          console.warn(
            `[webrtc] silence-watcher: Bot still seit ${Math.round(
              (Date.now() - this.lastUserFinalAt) / 1000,
            )}s, aber quality=${this.quality.local} → KEIN Plugin-Reconnect (Netz-Problem, #125)`,
          );
          this.lastUserFinalAt = 0; // Timer neu starten statt trennen
          return;
        }
        // #125: höchstens ein Watchdog-Reconnect pro 90s (geteiltes Gate).
        if (!this.watchdogReconnectAllowed("silence-watcher(warten)")) {
          this.lastUserFinalAt = 0;
          return;
        }
        console.warn(
          `[webrtc] silence-watcher: User wartet ${Math.round(
            (Date.now() - this.lastUserFinalAt) / 1000,
          )}s auf Bot-Reaktion (quality=${this.quality.local}) → Plugin tot, force reconnect`,
        );
        // Pre.450/468: User-Feedback bevor wir reconnecten.
        this.callbacks.onError?.(
          new Error(
            "Bot reagiert nicht (Gemini-Live-Plugin nach Tool-Call hängengeblieben — bekanntes Google-Limit). Session wird neu gestartet (~3s)…",
          ),
        );
        this.lastUserFinalAt = 0;
        // Pre.973: Der Silence-Watcher will einen FRISCHEN Agent (die Session
        // sitzt fest). Reconnect daher mit frischem Raum erzwingen — sonst
        // würde der Recovery-Reconnect denselben toten Raum wieder anfordern
        // (Dauer-Dead-Call). DAS ist das Sicherheitsnetz für „Reuse landete in
        // einem leeren Raum".
        this.forceFreshRoomNextConnect = true;
        this.stopSilenceWatcher();
        this.stopKeepAlive();
        const r = this.room;
        this.room = null;
        void r.disconnect().catch(() => undefined);
        return;
      }

      // Pre.446: Force-Reconnect bei zwei Schwellen
      //   - botActive (speaking/thinking) + 25s stumm → tote Session
      //   - listening + 60s stumm → Provider-Idle-Cut
      const threshold = botActive
        ? VoiceClient.SILENCE_THRESHOLD_BOT_ACTIVE_MS
        : this.state === "listening"
          ? VoiceClient.SILENCE_THRESHOLD_LISTENING_MS
          : null;
      if (threshold === null) {
        // idle / connecting / reconnecting / error → Stille normal
        this.lastBotActivityAt = Date.now();
        return;
      }
      // P1.1 (Audit): Im listening-State nur als tot werten, wenn der
      // USER seit der letzten Bot-Aktivität wirklich gesprochen hat —
      // beidseitige Stille ist kein Fehler und löst keinen Reconnect aus.
      if (!botActive && this.lastLocalSpeechAt <= this.lastBotActivityAt) {
        this.lastBotActivityAt = Date.now();
        return;
      }
      if (idleMs >= threshold) {
        // Ticket #124: #125 schützte nur den Warten-Pfad oben — DIESER
        // Stumm-Pfad (25s/60s) feuerte bei schlechtem Netz weiter zyklisch.
        // Bei poor/lost kommt Bot-Audio oft nur nicht DURCH (Leitung, nicht
        // Session tot) → erst ab doppelter Schwelle als tot werten.
        if (
          (this.quality.local === "poor" || this.quality.local === "lost") &&
          idleMs < threshold * 2
        ) {
          return;
        }
        // Ticket #124: gleiches 90s-Cooldown-Gate wie die anderen Watchdogs.
        if (!this.watchdogReconnectAllowed("silence-watcher(stumm)")) {
          this.lastBotActivityAt = Date.now();
          return;
        }
        console.warn(
          `[webrtc] silence-watcher: Bot ${Math.round(idleMs / 1000)}s stumm im State=${this.state} → force reconnect`,
        );
        // Pre.973: frischer Agent gewollt (Session tot/stumm) → frischen Raum
        // erzwingen, sonst Reuse-in-toten-Raum-Schleife (siehe Warten-Pfad).
        this.forceFreshRoomNextConnect = true;
        this.stopSilenceWatcher();
        this.stopKeepAlive();
        const r = this.room;
        this.room = null;
        void r.disconnect().catch(() => undefined);
      }
    }, VoiceClient.SILENCE_WATCHER_MS);
  }

  private stopSilenceWatcher(): void {
    if (this.silenceWatcherTimer) {
      clearInterval(this.silenceWatcherTimer);
      this.silenceWatcherTimer = null;
    }
  }

  /**
   * Pre.485 (A) — Mic-Bytes-Watchdog.
   *
   * Liest alle 15s WebRTC-getStats() vom LocalAudioTrack und prüft ob
   * bytesSent wächst. Wenn 30s+ keine Bytes mehr → Mic ist tot. Plus:
   * präemptiver Reconnect bei 9min Session-Alter (vor 10min-Provider-Cut).
   */
  private startMicWatchdog(): void {
    this.stopMicWatchdog();
    this.micWatchdogTimer = setInterval(() => {
      void this.checkMicHealth();
    }, VoiceClient.MIC_BYTES_CHECK_INTERVAL_MS);
  }

  private stopMicWatchdog(): void {
    if (this.micWatchdogTimer) {
      clearInterval(this.micWatchdogTimer);
      this.micWatchdogTimer = null;
    }
  }

  private async checkMicHealth(): Promise<void> {
    const room = this.room;
    if (!room) return;
    // P2.2 (Audit): Im Mute fließen bewusst keine Bytes — den Stale-
    // Zähler auffrischen, sonst feuert direkt nach dem Unmute ein
    // falscher mic-bytes-stale-Reconnect.
    if (this.micMuted) {
      this.lastLocalBytesChangedAt = Date.now();
      return;
    }

    // Pre.644: Preemptiver Refresh — aber NICHT mehr mitten im Satz.
    // Ab SOFT (8.5min) auf einen Sprech-Pause-Moment warten (Bot hört,
    // User redet gerade nicht). Ab HARD (9.5min) erzwingen wir es zur Not;
    // der Server-Flush (pre.636) rettet dann den letzten Zwischen-Transkript.
    const ageMs = Date.now() - this.sessionStartedAt;
    // pre.888: „LANGE Gespräche" (#compress) → vorsorgliche Auffrischung AUS
    // (Schwellen unendlich), Session läuft bis zu Geminis natürlicher Grenze.
    // Sonst feste Produktions-Schwellen (der ?rctest-Zweig ist raus).
    const inf = Number.POSITIVE_INFINITY;
    const SOFT_MS = this.longSessionMode
      ? inf
      : VoiceClient.PREEMPTIVE_SOFT_AGE_MS;
    const HARD_MS = this.longSessionMode
      ? inf
      : VoiceClient.PREEMPTIVE_HARD_AGE_MS;
    const VHARD_MS = this.longSessionMode
      ? inf
      : VoiceClient.PREEMPTIVE_VERYHARD_AGE_MS;
    if (ageMs >= SOFT_MS) {
      // Pre.769: ~1 Min vor der Auffrischung EINMAL den dezenten Amber-Puls
      // am Timer auslösen (lautloser Heads-up, kein Eingriff ins Gespräch).
      if (!this.preemptiveWarned) {
        this.preemptiveWarned = true;
        this.callbacks.onPreemptiveWarn?.();
      }
      // Pre.769: NIE den laufenden Bot-Satz kappen. Bevorzugt in echter Pause;
      // ab HARD (9min) reicht "Bot redet gerade nicht" (User-Worte sind via
      // Flush + kumulativem Transkript gesichert); erst ab VERY_HARD (9:45)
      // erzwingen wir auch mitten im Satz — letzter Ausweg vor dem ~10min-Cut.
      const veryHard = ageMs >= VHARD_MS;
      const hard = ageMs >= HARD_MS;
      const botSpeaking = this.botSpeakingTracks.size > 0;
      const safePause = this.state === "listening" && !this.userSpeaking;
      if (safePause || (hard && !botSpeaking) || veryHard) {
        const forced = veryHard;
        console.log(
          `[webrtc] preemptive refresh: Session ${Math.round(ageMs / 1000)}s alt, ${forced ? "VERY-HARD (erzwungen, evtl. im Satz)" : safePause ? "Sprech-Pause" : "Bot-Pause"} → vor ~10min-Provider-Cut nahtlos neu aufbauen`,
        );
        // Dezenter Mikro-Hinweis fürs UI (KEIN Crash-/Wackel-Banner) +
        // Flag, damit scheduleReconnect den lauten Toast/Chime unterdrückt.
        this.preemptiveRefreshInFlight = true;
        this.callbacks.onPreemptiveRefresh?.(forced);
        this.triggerForceReconnect(
          forced ? "preemptive-veryhard-deadline" : "preemptive-9min-pause",
        );
        return;
      }
      // SOFT/HARD erreicht, aber gerade kein guter Moment (Bot/User reden) →
      // diesen 15s-Tick aussetzen und beim nächsten erneut auf Pause prüfen.
    }

    // bytesSent vom Local Audio Track via getStats()
    try {
      const pubs = Array.from(
        room.localParticipant.audioTrackPublications.values(),
      );
      const pub = pubs.find((p) => p.track);
      const sender = pub?.track?.sender;
      if (!sender) {
        // Pre.837 (Audit P1.3): Track wurde NIE publiziert (iOS-Mikro-
        // Sperre!) — der Watchdog war hier blind, der Call stand ewig als
        // „verbunden" (7 Log-Sessions). Nach 2×15s: klare MIC_BLOCKED-
        // Meldung; max. 2 Reconnect-Versuche, dann nur noch Banner
        // (keine Endlos-Schleife gegen eine OS-Sperre).
        if (this.state === "listening" && ++this.noSenderTicks >= 2) {
          this.noSenderTicks = 0;
          this.callbacks.onError?.(new Error("MIC_BLOCKED"));
          if (++this.micBlockedReconnects <= 2) {
            this.triggerForceReconnect("mic-never-published");
          }
        }
        return;
      }
      this.noSenderTicks = 0;
      const stats = await sender.getStats();
      let bytesSent = 0;
      stats.forEach((report) => {
        const r = report as { type?: string; bytesSent?: number };
        if (r.type === "outbound-rtp" && typeof r.bytesSent === "number") {
          bytesSent += r.bytesSent;
        }
      });
      const now = Date.now();
      if (bytesSent > this.lastLocalBytesSent) {
        this.lastLocalBytesSent = bytesSent;
        this.lastLocalBytesChangedAt = now;
        return;
      }
      const staleMs = now - this.lastLocalBytesChangedAt;
      // Nur reconnecten wenn State === "listening" (in idle/connecting/
      // reconnecting/speaking ist Mic-Pause erwartet bzw. wir sind schon
      // dran).
      if (
        staleMs >= VoiceClient.MIC_BYTES_STALE_THRESHOLD_MS &&
        this.state === "listening"
      ) {
        console.warn(
          `[webrtc] mic-watchdog: LocalAudio ${Math.round(staleMs / 1000)}s keine bytes → reconnect`,
        );
        this.triggerForceReconnect("mic-bytes-stale");
      }
    } catch (err) {
      console.warn("[webrtc] mic-watchdog: getStats failed:", err);
    }
  }

  /**
   * Pre.485 (B) — Visibility-Resume.
   *
   * Wenn Tab >2min hidden war und kommt zurück: WebRTC ist wahrscheinlich
   * gedriftet (iOS sperrt Mic im Hintergrund, Mobile-NAT-Timeout, etc.).
   * Force-reconnect statt zu hoffen dass es weiterläuft.
   */
  private attachVisibilityHandler(): void {
    this.detachVisibilityHandler();
    if (typeof document === "undefined") return;
    this.onVisibilityChange = (): void => {
      if (!this.room) return;
      if (document.visibilityState === "hidden") {
        this.hiddenSinceTs = Date.now();
        return;
      }
      if (document.visibilityState === "visible" && this.hiddenSinceTs > 0) {
        const hiddenMs = Date.now() - this.hiddenSinceTs;
        this.hiddenSinceTs = 0;
        if (hiddenMs >= VoiceClient.VISIBILITY_HIDDEN_RECONNECT_MS) {
          console.log(
            `[webrtc] visibility-resume: war ${Math.round(hiddenMs / 1000)}s hidden → reconnect`,
          );
          this.triggerForceReconnect("visibility-resume");
        }
      }
    };
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  private detachVisibilityHandler(): void {
    if (this.onVisibilityChange && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
    }
    this.onVisibilityChange = null;
    this.hiddenSinceTs = 0;
  }

  /**
   * Pre.485: Einheitlicher Force-Reconnect-Pfad. Stoppt alle Watchdogs,
   * disconnected die Room (Disconnected-Handler triggert scheduleReconnect).
   */
  /** P1.1 (Audit): app.ts meldet hier ≥1s zusammenhängende User-Sprache. */
  noteLocalSpeech(): void {
    this.lastLocalSpeechAt = Date.now();
  }

  /** P1.6 (Audit): In-place-Reconnect von außen (z.B. Provider-Drop). */
  forceReconnect(reason: string): void {
    this.triggerForceReconnect(reason);
  }

  private triggerForceReconnect(reason: string): void {
    if (!this.room) {
      // 2026-07-22 (Martin, "Anruf laeuft, reagiert aber nicht"): nach
      // einem Hintergrund-/Netz-Abriss ist room bereits null — frueher
      // kehrte dieser Pfad STILL zurueck (der Banner verschwand, der
      // Timer lief weiter, nichts verband sich neu; als offen notiert
      // seit pre.1710). Jetzt: direkt frisch wiederverbinden.
      console.log(`[webrtc] force-reconnect ohne Raum (${reason}) — frischer Verbindungsaufbau`);
      this.forceFreshRoomNextConnect = true;
      this.scheduleReconnect();
      return;
    }
    console.log(`[webrtc] force-reconnect triggered: ${reason}`);
    // Pre.973: Force-/Preemptive-Reconnects wollen einen FRISCHEN Agent (neue
    // Session), NICHT das Wiederanknüpfen an die alte — also frischer Raum.
    this.forceFreshRoomNextConnect = true;
    this.stopSilenceWatcher();
    this.stopMicWatchdog();
    this.stopKeepAlive();
    this.detachVisibilityHandler();
    // #188: stale Bot-Segmente JETZT räumen (wie im sauberen disconnect, Z.1413)
    // — sonst mischen sich nach dem Reconnect alte mit neuen Transkript-Segmenten
    // → abgeschnittener/zerstückelter Text. Löscht nur den laufenden Reply-
    // Akkumulator + Echo-Grace, NICHT die gespeicherte Historie.
    this.resetBotSegments();
    const r = this.room;
    this.room = null;
    void r.disconnect().catch(() => undefined);
  }

  /**
   * Pre.446: iOS-Safari-Autoplay-Unblock — komplett umgebaut.
   *
   * Pre.445 hatte muted=true + volume=0 gesetzt. iOS interpretiert das
   * als "muted autoplay" und schaltet damit NUR muted-Audio frei — der
   * spätere Greeting-MP3 mit volume=1 wurde weiter geblockt.
   *
   * Pre.446 Fix: volume=0.001 (unmuted, praktisch unhörbar). Das ist
   * für iOS ein "unmuted user-gesture play" und schaltet alles frei.
   *
   * Zusätzlich: persistentes <audio> Element im DOM (statt new Audio()),
   * weil iOS Standalone-PWA-Mode bei frisch konstruierten Audio-Elementen
   * empfindlicher ist als bei DOM-vorhandenen.
   */
  private primeAudioPlayback(): Promise<void> {
    return new Promise((resolve) => {
      try {
        // ~10ms silent MP3, data-URI
        const silent =
          "data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA/+M4wAAAAAAAAAAAAEluZm8AAAAPAAAAAwAAAbAAqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqv////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAQwAAAAAAAAAbBpUQRkAAAAAAD/+xDEAAPAAAGkAAAAIAAANIAAAARMQU1FMy4xMDBVVVVVVVVVVVVVVVVVVVVVVVVVVVU=";
        // Persistentes Element wiederverwenden (iOS mag das mehr als frisch
        // konstruierte). Erstmal anlegen, beim nächsten Prime wiederverwenden.
        let a = document.getElementById(
          "voice-audio-prime",
        ) as HTMLAudioElement | null;
        if (!a) {
          a = document.createElement("audio");
          a.id = "voice-audio-prime";
          a.hidden = true;
          a.setAttribute("playsinline", "true");
          document.body.appendChild(a);
        }
        a.src = silent;
        // KRITISCH: NICHT muted, volume nicht 0 — sonst wertet iOS das als
        // muted-autoplay-Mode und schaltet unmuted nicht frei.
        a.muted = false;
        a.volume = 0.001;
        const cleanup = (): void => {
          try {
            a.pause();
          } catch {
            /* ignore */
          }
          resolve();
        };
        a.addEventListener("ended", cleanup, { once: true });
        a.addEventListener("error", cleanup, { once: true });
        a.play()
          .then(() => {
            setTimeout(cleanup, 200);
          })
          .catch((err) => {
            console.warn("[voice] primeAudioPlayback failed:", err);
            cleanup();
          });
      } catch (err) {
        console.warn("[voice] primeAudioPlayback exception:", err);
        resolve();
      }
    });
  }

  /**
   * Pre.444: Quality-Change-Handler. Wird von RoomEvent.ConnectionQualityChanged
   * gerufen, separat für Local- und Remote-Participants.
   *
   * Aktionen abhängig vom side:
   * - Local: trigger adaptive Bitrate, Poor-Warning, Preemptive-Reconnect
   * - Remote: nur Snapshot updaten (für UI-Anzeige der Gegenseite)
   */
  private handleQualityChange(
    level: QualityLevel,
    participant: Participant | undefined,
    room: Room,
  ): void {
    const isLocal = participant?.identity === room.localParticipant.identity;
    if (isLocal) {
      const prev = this.quality.local;
      this.quality = { ...this.quality, local: level };
      if (prev !== level) {
        this.localQualityEnteredAt = Date.now();
        // poor-warning reset wenn wir wieder über "poor" hinaus sind
        if (level !== "poor" && level !== "lost") {
          this.poorWarningSent = false;
        }
      }
      this.callbacks.onConnectionQuality?.(this.quality);
      this.applyAdaptiveBitrate(level, room);
      this.checkPoorConnection(level);
      this.checkPreemptiveReconnect(level);
    } else {
      this.quality = { ...this.quality, remote: level };
      this.callbacks.onConnectionQuality?.(this.quality);
    }
  }

  /**
   * Pre.444: Adaptive Bitrate. Bei "poor" runter auf 12 kbps Opus (immer
   * noch sprachverständlich, doppelt so robust gegen Packet-Loss). Bei
   * "good"/"excellent" zurück auf 24 kbps. Debounced 3s — sonst flackert
   * der Codec bei oszillierender Quality.
   */
  private applyAdaptiveBitrate(level: QualityLevel, room: Room): void {
    const targetBucket: "high" | "low" = level === "poor" ? "low" : "high";
    if (targetBucket === this.currentBitrateBucket) {
      if (this.bitrateChangeTimer) {
        clearTimeout(this.bitrateChangeTimer);
        this.bitrateChangeTimer = null;
      }
      return;
    }
    if (this.bitrateChangeTimer) {
      clearTimeout(this.bitrateChangeTimer);
    }
    this.bitrateChangeTimer = setTimeout(() => {
      this.bitrateChangeTimer = null;
      const pubs = room.localParticipant?.audioTrackPublications;
      if (!pubs) return;
      const bps =
        targetBucket === "low"
          ? VoiceClient.BITRATE_LOW_KBPS
          : VoiceClient.BITRATE_HIGH_KBPS;
      for (const pub of pubs.values()) {
        const track = pub.track as LocalAudioTrack | undefined;
        const sender = track?.sender;
        if (!sender) continue;
        try {
          const params = sender.getParameters();
          if (!params.encodings || params.encodings.length === 0) {
            params.encodings = [{}];
          }
          for (const enc of params.encodings) {
            enc.maxBitrate = bps;
          }
          sender.setParameters(params).catch((err: unknown) => {
            console.warn("[voice] setParameters failed:", err);
          });
        } catch (err) {
          console.warn("[voice] adaptive-bitrate setParameters error:", err);
        }
      }
      this.currentBitrateBucket = targetBucket;
      console.info(
        `[voice] adaptive bitrate → ${targetBucket} (${bps} bps, quality=${level})`,
      );
    }, VoiceClient.BITRATE_DEBOUNCE_MS);
  }

  /** Pre.444: Toast wenn 5s+ in poor — nur 1× pro Session. */
  private checkPoorConnection(level: QualityLevel): void {
    if (level !== "poor") return;
    if (this.poorWarningSent) return;
    setTimeout(() => {
      if (this.quality.local === "poor" && !this.poorWarningSent) {
        this.poorWarningSent = true;
        this.callbacks.onPoorConnection?.();
      }
    }, VoiceClient.POOR_WARN_MS);
  }

  /**
   * Pre.444: Wenn die lokale Quality 15s+ in "poor" steckt UND der Bot
   * gleichzeitig still ist (kein Audio-Frame in den letzten 5s), präemptiv
   * disconnect+reconnect — bevor's komplett wegbricht.
   */
  private checkPreemptiveReconnect(level: QualityLevel): void {
    if (level !== "poor") return;
    setTimeout(() => {
      if (this.quality.local !== "poor") return;
      const stuckMs = Date.now() - this.localQualityEnteredAt;
      const botStaleMs = Date.now() - this.lastBotActivityAt;
      if (
        stuckMs >= VoiceClient.PREEMPTIVE_RECONNECT_MS &&
        botStaleMs > 5_000 &&
        this.room
      ) {
        // #125: gleiches 90s-Gate — ein neuer Verbindungsaufbau repariert
        // kein schlechtes Funknetz (reconnect → wieder poor → Schleife).
        if (!this.watchdogReconnectAllowed("preemptive(poor)")) {
          return;
        }
        console.warn(
          `[voice] preemptive reconnect (quality=poor ${Math.round(stuckMs / 1000)}s, bot-still ${Math.round(botStaleMs / 1000)}s)`,
        );
        // Pre.973: Recovery-Reconnect → frischer Raum/Agent (wie alle Watchdog-
        // Pfade, = heutiges Verhalten). Verhindert, dass dieser Reconnect einen
        // evtl. toten Raum wiederverwendet (Reuse-Schleife) statt sauber neu
        // aufzubauen. Nahtloser Reuse bleibt dem reinen, sauberen Drop vorbehalten.
        this.forceFreshRoomNextConnect = true;
        const r = this.room;
        this.room = null;
        try {
          r.disconnect();
        } catch {
          /* ignore — Disconnected-Handler triggert scheduleReconnect */
        }
      }
    }, VoiceClient.PREEMPTIVE_RECONNECT_MS);
  }

  /** Wird von Audio-/Transcript-/Data-Events gerufen, resetet Stille-Timer. */
  private markBotActivity(): void {
    this.lastBotActivityAt = Date.now();
  }

  /**
   * Schaltet die Bot-Audio-Wiedergabe stumm (Text-Mode) oder hörbar (Voice-
   * Mode). Wirkt auf alle bereits gepublishten Bot-Tracks UND auf zukünftige
   * (das flag wird bei TrackSubscribed angewendet). LiveKit empfängt den
   * Stream weiterhin — nur die Audio-Wiedergabe lokal ist stumm. Bandwidth-
   * mäßig nicht ideal, dafür ohne Server-Roundtrip umschaltbar.
   */
  setBotAudioMuted(muted: boolean): void {
    this.botAudioMuted = muted;
    if (!this.room) return;
    for (const track of this.botSpeakingTracks) {
      const elements = track.attachedElements;
      for (const el of elements) {
        if (el instanceof HTMLAudioElement) el.muted = muted;
      }
    }
  }

  async setMicEnabled(enabled: boolean): Promise<void> {
    // pre.910 (Audit P0-1/2): Wunsch merken, BEVOR wir abbrechen — er muss
    // Reconnects überleben (connectInner reappliziert ihn).
    this.desiredMicEnabled = enabled;
    if (!this.room) return;
    await this.room.localParticipant.setMicrophoneEnabled(enabled);
    this.forwardLocalMicTrack();
    // P2.2 (Audit): KEIN setState mehr — Mute lügt nicht länger "idle".
    // Banner, Timer und Sicherungs-Indikator bleiben sichtbar.
    this.micMuted = !enabled;
  }

  /**
   * 2026-06-30 (Martin): Echter Verbindungs-Status — true, solange ein
   * LiveKit-Room aktiv und nicht getrennt ist. Dient der Selbstheilung
   * haengender connected-Flags im UI (Anrufen/PTT wurden sonst taub).
   */
  isConnected(): boolean {
    return this.room !== null && String(this.room.state) !== "disconnected";
  }

  async disconnect(): Promise<void> {
    // Pre.207 (B): User-initiierter Disconnect — kein Auto-Reconnect.
    this.userInitiatedDisconnect = true;
    this.laufenderAnruf = null;
    this.reconnectAttempts = 0;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    // Pre.337: Keep-Alive + Stummheits-Watcher sauber abreißen.
    this.stopKeepAlive();
    this.stopSilenceWatcher();
    // Pre.485: Mic-Watchdog + Visibility-Handler ebenfalls aufräumen
    this.stopMicWatchdog();
    this.detachVisibilityHandler();
    this.resetBotSegments();
    if (this.room) {
      // P2.6 (Audit): Bot-Audio ~90ms ausblenden BEVOR der Room stirbt —
      // der harte Schnitt mitten im Bot-Wort war eine Knacks-Quelle beim
      // Auflegen. (iOS ignoriert element.volume — dort bleibt der Rest
      // offen, bis Bot-Audio durch einen WebAudio-GainNode läuft / P3.2.)
      if (this.botSpeakingTracks.size > 0) {
        try {
          const els: HTMLMediaElement[] = [];
          for (const t of this.botSpeakingTracks) {
            els.push(...(t.attachedElements));
          }
          const t0 = performance.now();
          await new Promise<void>((resolve) => {
            const fade = (): void => {
              const k = Math.min(1, (performance.now() - t0) / 90);
              for (const a of els) a.volume = Math.max(0, 1 - k);
              if (k < 1) requestAnimationFrame(fade);
              else resolve();
            };
            requestAnimationFrame(fade);
          });
        } catch {
          /* Fade ist Komfort — Teardown geht weiter */
        }
      }
      await this.room.disconnect();
      this.room = null;
    }
    this.callbacks.onLocalAudio?.(null);
    this.setState("idle");
  }

  /**
   * Pre.217: Akkumuliert ein Bot-Transcription-Segment in der current-reply-
   * Map und emittiert onBotMessage mit dem JOIN aller Segmente. Final-Marker
   * setzt einen Debounce-Timer (1.5s) — wenn keine neuen Segmente nachkommen,
   * gilt die Reply als komplett und onBotMessage(fullText, true) feuert.
   */
  private handleBotSegment(segmentId: string, text: string): void {
    // Pre.310: Realtime-Garbage-Token-Filter. Gemini Live emittiert
    // gelegentlich Control-Sequenzen wie "<ctrl46>" wenn tool_calls
    // gestreamt werden und die Pipeline einen Token-Range zurück
    // schickt der als Text-Delta ankommt. Diese sind WERTLOS für den
    // User und sollten weder gerendert noch in History persistiert
    // werden. Wenn nach Filter NICHTS übrig bleibt → kompletter Skip.
    const CTRL_TOKEN_RE = /<\/?(?:ctrl|tool[a-z]*)?\d+>/gi;
    const sanitized = text
      .replace(CTRL_TOKEN_RE, "")
      .replace(/[ \t]{2,}/g, " ")
      .trim();
    if (!sanitized) {
      return;
    }
    // Pre.281 Bug #193 Fix: LiveKit kann denselben Text mit verschiedenen
    // Segment-IDs schicken (z.B. preliminary + final-Segment). Vor dem
    // `set`: existing Segments anschauen — wenn der neue text einen
    // existing-text-Wert ENTHÄLT (Bot hat geredet, finalisiert kompletten
    // Reply mit neuer ID), entferne die ID-Subset-Einträge. Sonst entstehen
    // doppelte Render-Bubbles wie "Tippe ... Tippe ..." oder doppelte
    // Such-Zusammenfassungen.
    text = sanitized;
    const incoming = sanitized;
    // #131: Verspätetes Echo-Fragment einer GERADE finalisierten Reply
    // verwerfen. Nur wenn KEINE neue Reply läuft (botSegments leer), im
    // Grace-Fenster, und das Fragment komplett in der letzten Reply steckt
    // (also nichts Neues bringt). Supersets/echte Fortsetzungen fließen
    // weiter durch → der Frontend-Präfix-Dedupe führt sie sauber zusammen.
    if (
      this.botSegments.size === 0 &&
      this.lastFinalText &&
      Date.now() - this.lastFinalAt < VoiceClient.BOT_LATE_ECHO_GRACE_MS &&
      this.lastFinalText.includes(incoming)
    ) {
      return;
    }
    if (incoming.length >= 8) {
      for (const [existingId, existingText] of this.botSegments) {
        if (existingId === segmentId) continue;
        const ex = existingText.trim();
        if (ex.length === 0) continue;
        // Neue Segment ist Superset → entferne kürzere existing
        if (incoming.includes(ex)) {
          this.botSegments.delete(existingId);
        }
        // Neue Segment ist Subset eines existing → ignoriere die neue
        else if (ex.includes(incoming)) {
          return;
        }
      }
    }
    this.botSegments.set(segmentId, text);
    const fullText = this.joinSegments();
    // Streaming-Update — Frontend rendert mit fillBubbleWithText.
    this.callbacks.onBotMessage?.(fullText, false);
    if (this.botFinalTimer) clearTimeout(this.botFinalTimer);
    this.botFinalTimer = setTimeout(() => {
      const final = this.joinSegments();
      this.botFinalTimer = null;
      if (final) {
        // #131: finalisierte Reply merken → späte Echo-Fragmente erkennen.
        this.lastFinalText = final;
        this.lastFinalAt = Date.now();
        this.callbacks.onBotMessage?.(final, true);
      }
      this.botSegments.clear();
    }, VoiceClient.BOT_REPLY_FINAL_DEBOUNCE_MS);
  }

  /**
   * Pre.793 (#66): Segmente STRUKTURERHALTEND zusammenfügen. Der alte
   * join(" ") verschmolz Markdown-Tabellenzeilen (LiveKit liefert sie als
   * eigene Segmente OHNE Newline) zu EINER Zeile — der Tabellen-Renderer
   * sah nie eine Tabelle, obwohl die DB-Kopie sauber war (48-Gesetze-
   * Vorfall). Tabellen-Zeilen (|…|) werden mit Newline angefügt, Prosa
   * weiter mit Leerzeichen.
   */
  private joinSegments(): string {
    let out = "";
    for (const seg of this.botSegments.values()) {
      if (!out) {
        out = seg;
        continue;
      }
      const tableSeg = /^\s*\|.*\|\s*$/.test(seg.trim());
      const tableTail = /\|\s*$/.test(out.trimEnd());
      out += (tableSeg || tableTail ? "\n" : " ") + seg;
    }
    return out.trim();
  }

  /**
   * Bot-Akkumulator zurücksetzen — z.B. bei Interrupt oder Disconnect, damit
   * der Debounce-Timer nicht eine veraltete Reply finalisiert.
   */
  private resetBotSegments(): void {
    this.botSegments.clear();
    if (this.botFinalTimer) {
      clearTimeout(this.botFinalTimer);
      this.botFinalTimer = null;
    }
    // #131: nach Interrupt/Disconnect kein Echo-Gedächtnis übertragen — die
    // nächste Reply ist ein frischer Kontext, das Grace-Fenster darf sie nie
    // versehentlich unterdrücken.
    this.lastFinalText = "";
    this.lastFinalAt = 0;
  }

  /**
   * Pre.207 (B): Plant einen Reconnect-Versuch nach unerwartetem Disconnect.
   * Exponential backoff (1s, 3s, 6s) + max 3 Versuche. Nach max-Tries gibt's
   * eine onError-Notification an den App-Layer.
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= VoiceClient.MAX_RECONNECT_ATTEMPTS) {
      console.warn("[voice] reconnect aufgegeben nach", this.reconnectAttempts);
      this.callbacks.onError?.(
        new Error("Verbindung zum Bot verloren — bitte erneut anrufen."),
      );
      this.reconnectAttempts = 0;
      this.userInitiatedDisconnect = false;
      // Pre.973: nach Aufgeben den gemerkten Raum verwerfen — der nächste
      // (manuelle) Anruf startet garantiert frisch.
      this.lastRoomName = null;
      this.laufenderAnruf = null;
      this.setState("idle");
      return;
    }
    this.reconnectAttempts++;
    // Pre.644: war das ein GEPLANTER preemptiver Refresh? Dann diesen ersten
    // Versuch leise fahren (kein "Verbindung wackelt"-Toast, kein Chime).
    // Flag sofort zurücksetzen — schlägt der Versuch fehl und wir landen
    // erneut hier, ist es echte Not und soll wieder normal/laut sein.
    const silentPreemptive = this.preemptiveRefreshInFlight;
    this.preemptiveRefreshInFlight = false;
    // Pre.443: 5 Versuche statt 3, mit schnellerem Start
    // (500ms, 1500ms, 3000ms, 5000ms, 8000ms).
    const delays = [500, 1500, 3000, 5000, 8000];
    let delay = delays[this.reconnectAttempts - 1] ?? 8000;
    // pre.877 (Ticket #148): NUR beim GEPLANTEN Refresh dem alten Worker etwas
    // mehr Zeit geben, den letzten Beitrag in die DB zu committen, BEVOR die neue
    // Session {{recentMessages}} liest — sonst Wettlauf-Verlust des letzten Turns.
    // Echte Fehler-Reconnects bleiben schnell (500ms).
    if (silentPreemptive && this.reconnectAttempts === 1) delay = 1200;
    console.warn(
      `[voice] ${silentPreemptive ? "preemptive-refresh" : "auto-reconnect"} #${this.reconnectAttempts} in ${delay}ms`,
    );
    // Pre.326: separater State "reconnecting" (amber) statt "connecting"
    // (blau) — damit der User sieht "Verbindung war weg, ich versuch's
    // nochmal" und nicht denkt es ist der erste Connect.
    this.setState("reconnecting");
    if (!silentPreemptive) {
      this.callbacks.onReconnectAttempt?.(
        this.reconnectAttempts,
        VoiceClient.MAX_RECONNECT_ATTEMPTS,
      );
    }
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect()
        .then(() => {
          this.reconnectAttempts = 0;
          // Beim geplanten Refresh KEIN lauter "wieder da"-Toast — die Pille
          // vom onPreemptiveRefresh hat den User bereits ruhig informiert.
          if (!silentPreemptive) this.callbacks.onReconnected?.();
        })
        .catch((err) => {
          console.warn("[voice] reconnect fehlgeschlagen:", err);
          // Ticket 2728: Hoechstdauer erreicht — nicht weiter versuchen, sondern
          // ruhig sagen warum (statt "Verbindung zum Bot verloren").
          if (err instanceof Error && err.message === ANRUF_HOECHSTDAUER) {
            this.reconnectAttempts = 0;
            this.lastRoomName = null;
            this.userInitiatedDisconnect = false;
            this.callbacks.onError?.(err);
            this.setState("idle");
            return;
          }
          this.scheduleReconnect();
        });
    }, delay);
  }

  /**
   * Schickt einen Text-Input an den Agent (Test-Tab). Der Agent reagiert wie
   * auf eine Voice-Frage — generiert Antwort + spricht sie über TTS.
   */
  /**
   * Schickt eine Bild-Referenz an den Agent. Der Agent bekommt das Bild
   * via Datenkanal-Message `voice-v8.image_input` und kann es in den
   * nächsten LLM-Call als Multi-Modal-Block einbinden (Vision).
   */
  async sendImageInput(url: string, caption?: string): Promise<void> {
    if (!this.room) {
      throw new Error("nicht verbunden");
    }
    const payload = JSON.stringify({
      type: "voice-v8.image_input",
      url,
      caption: caption ?? null,
    });
    await this.room.localParticipant.publishData(
      new TextEncoder().encode(payload),
      { reliable: true, topic: "voice-v8" },
    );
  }

  async sendTextInput(text: string): Promise<void> {
    if (!this.room) {
      throw new Error("nicht verbunden");
    }
    const payload = JSON.stringify({
      type: "voice-v8.test_input",
      text,
    });
    await this.room.localParticipant.publishData(
      new TextEncoder().encode(payload),
      { reliable: true, topic: "voice-v8" },
    );
  }

  /**
   * Bricht die aktuell laufende Bot-Speech sofort ab. Der Bot hört auf zu
   * sprechen, der ChatCtx wird auf den abgeschnittenen Stand fixiert.
   *
   * Wir setzen den State sofort lokal auf "listening" — LiveKit unsubscribed
   * den Track NICHT bei interrupt (nur den Audio-Stream), darum kommt unser
   * `TrackUnsubscribed`-Handler nicht. Ohne diesen lokalen Force würde der
   * Stop-Button optisch hängen bleiben.
   */
  async interruptBot(): Promise<void> {
    if (!this.room) return;
    const payload = JSON.stringify({ type: "voice-v8.interrupt" });
    await this.room.localParticipant.publishData(
      new TextEncoder().encode(payload),
      { reliable: true, topic: "voice-v8" },
    );
    this.botSpeakingTracks.clear();
    this.resetBotSegments();
    this.setState("listening");
    this.callbacks.onBotAudio?.(null);
  }

  private forwardLocalMicTrack(): void {
    if (!this.room) return;
    const pubs = Array.from(
      this.room.localParticipant.audioTrackPublications.values(),
    );
    const localPub = pubs.find(
      (p) => p.source === Track.Source.Microphone && !p.isMuted,
    );
    const track = localPub?.audioTrack;
    this.callbacks.onLocalAudio?.(track?.mediaStreamTrack ?? null);
  }

  private handleDataPacket(payload: Uint8Array): void {
    try {
      const text = new TextDecoder().decode(payload);
      const msg = JSON.parse(text) as Record<string, unknown>;
      // P1.1 (Audit): Nur ECHTE Bot-Lebenszeichen zählen für den
      // SilenceWatcher — Pongs/Telemetrie ausdrücklich nicht.
      const t = typeof msg.type === "string" ? msg.type : "";
      if (
        /^voice-v8\.(greeting|greeting-tts|state|tool_card|tool_progress|truth_warning|error)$/.test(
          t,
        )
      ) {
        this.markBotActivity();
      }
      if (msg.type === "voice-v8.error" && typeof msg.message === "string") {
        // Pre.227: Server-Side Realtime/Pipeline-Error → Frontend rendert
        // System-Bubble damit der User sieht warum der Bot stumm ist.
        // Pre.296: Wenn der User gerade selbst aufgelegt hat, ist ein
        // [session-close]-Error vom Server harmlos (Plugin-Reconnect-Race
        // nach goAway). Toast unterdrücken.
        if (this.userInitiatedDisconnect) {
          return;
        }
        const src = typeof msg.source === "string" ? msg.source : "session";
        this.callbacks.onError?.(
          new Error(`[${src}] ${String(msg.message).slice(0, 280)}`),
        );
      } else if (
        msg.type === "voice-v8.greeting-tts" &&
        typeof msg.text === "string"
      ) {
        // Pre.408: Gemini-Realtime kann generateReply nicht. Agent
        // schickt Chirp3-HD-Voice-ID die EXAKT der Gemini-Live-Voice
        // entspricht (Aoede → de-DE-Chirp3-HD-Aoede etc.) — somit
        // klingt Greeting wie Live-Chat. Frontend holt MP3 von
        // /api/voice-preview und spielt ab. Mikro bleibt offen.
        const greetText = msg.text;
        const greetVoiceId =
          typeof msg.voiceId === "string"
            ? msg.voiceId
            : "de-DE-Chirp3-HD-Aoede";
        void (async () => {
          try {
            const r = await fetch("/api/voice-preview", {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                voiceId: greetVoiceId,
                text: greetText,
                ttsProvider: "google",
              }),
            });
            if (!r.ok) {
              console.warn("[greeting-tts] voice-preview", r.status);
              this.callbacks.onBotMessage?.(greetText, true);
              return;
            }
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            // Pre.447 Ticket #6: Statt frisch new Audio() das schon im
            // connect() geprimete #voice-audio-prime Element wiederverwenden.
            // iOS hat dieses Element bereits als "audio-allowed" registriert
            // (User-Gesture-Klammer) → src tauschen und play() klappt ohne
            // Autoplay-Block.
            const audio =
              (document.getElementById(
                "voice-audio-prime",
              ) as HTMLAudioElement | null) ?? new Audio();
            audio.src = url;
            audio.muted = false;
            audio.volume = 1.0;
            audio.setAttribute("playsinline", "true");
            audio.onended = () => URL.revokeObjectURL(url);
            audio.onerror = () => URL.revokeObjectURL(url);
            await audio.play().catch((err) => {
              console.warn("[greeting-tts] play failed:", err);
              // Pre.449: KEIN onAudioStuck mehr bei greeting-tts-Failure!
              // Ticket #9, #11: User sieht Banner aber kann TROTZDEM normal
              // mit Bot reden → Banner war Fehlalarm. Der LiveKit-Audio-Track
              // funktioniert auch wenn die MP3-Bridge failed.
              // Stattdessen: silent skip — der Greeting-Text steht eh schon
              // sichtbar im Chat, der nächste echte Bot-Reply (via LiveKit-
              // Track) wird hörbar sein.
            });
            this.callbacks.onBotMessage?.(greetText, true);
          } catch (err) {
            console.warn("[greeting-tts] failed:", err);
            this.callbacks.onBotMessage?.(greetText, true);
          }
        })();
      } else if (
        msg.type === "voice-v8.greeting" &&
        typeof msg.text === "string"
      ) {
        // Pre.331 Fix — Doppel-Bubble-Bug:
        // Wenn Realtime-Mode (Gemini Live / OpenAI Realtime) sowohl
        // TranscriptionSegments (für STT-Streaming) als auch ein
        // `voice-v8.greeting`-Data-Packet schickt, konkurrieren beide
        // um liveBotBubble:
        //   1. greeting kommt → onBotMessage(text, isFinal=true) → Bubble #1
        //   2. Debounce-Timer von handleBotSegment() läuft noch (1500ms)
        //   3. Timer feuert → onBotMessage(akkumulierter Text, true) → Bubble #2
        // Resultat: dasselbe Reply zweimal mit kleinen Unterschieden im Text.
        //
        // Fix: BEVOR wir die greeting-Message emittieren, killen wir den
        // pending Debounce-Timer und clearen den Segment-Akkumulator —
        // damit der Akkumulator nicht nachträglich eine zweite Bubble
        // erzeugt. Der greeting-Text gewinnt (er ist der vom Server
        // bestätigte finale Text).
        this.resetBotSegments();
        this.callbacks.onBotMessage?.(msg.text, true);
      } else if (
        msg.type === "voice-v8.state" &&
        typeof msg.text === "string"
      ) {
        // P2.3 (Audit, defensiv): "idle" bewusst NICHT mehr erlaubt — der
        // Server darf einen laufenden Call nie in idle drücken. (Der Agent
        // sendet voice-v8.state derzeit ohnehin nirgends.)
        if (
          msg.text === "thinking" ||
          msg.text === "listening" ||
          msg.text === "speaking"
        ) {
          this.setState(msg.text);
        }
      } else if (msg.type === "voice-v8.tool_card") {
        this.callbacks.onToolCard?.({
          tool: String(msg.tool ?? ""),
          callId: String(msg.callId ?? ""),
          isError: Boolean(msg.isError),
          result: msg.result,
          anzeige: msg.anzeige,
        });
      } else if (msg.type === "voice-v8.tool_progress") {
        // Pre.339: Live-Progress-Updates (claude_task_server u.a.)
        const phaseRaw = msg.phase;
        const phase =
          phaseRaw === "started" ||
          phaseRaw === "running" ||
          phaseRaw === "done"
            ? phaseRaw
            : "running";
        this.callbacks.onToolProgress?.({
          tool: String(msg.tool ?? ""),
          callId: typeof msg.callId === "string" ? msg.callId : undefined,
          phase,
          elapsedMs:
            typeof msg.elapsedMs === "number" ? msg.elapsedMs : undefined,
          lastLine: typeof msg.lastLine === "string" ? msg.lastLine : undefined,
        });
      } else if (msg.type === "voice-v8.truth_warning") {
        this.callbacks.onTruthWarning?.({
          reason: typeof msg.reason === "string" ? msg.reason : undefined,
          correction:
            typeof msg.correction === "string" ? msg.correction : undefined,
          latencyMs:
            typeof msg.latencyMs === "number" ? msg.latencyMs : undefined,
        });
      } else if (
        msg.type === "voice-v8.user_partial" &&
        typeof msg.text === "string"
      ) {
        // Ticket #155: Live-Mitschrift langer Monologe. Der Agent pusht den
        // wachsenden (noch nicht finalen) User-Zwischenstand, weil Gemini-Live
        // finale Transkripte erst in ~30s-Paketen liefert. Wir speisen ihn in
        // den BESTEHENDEN onUserTranscript-Pfad (isFinal=false) — er aktualisiert
        // die EINE wachsende Live-User-Bubble, ohne in die History zu schreiben.
        // Das finale Transkript (LiveKit TranscriptionReceived isFinal) ersetzt
        // sie über denselben Pfad → keine doppelten finalen Nachrichten.
        if (msg.text.trim().length > 0) {
          this.callbacks.onUserTranscript?.(msg.text, false);
        }
      } else if (msg.type === "voice-v8.user_state") {
        if (
          msg.state === "speaking" ||
          msg.state === "listening" ||
          msg.state === "away"
        ) {
          // Pre.644: für den preemptiven Refresh merken, ob der User gerade
          // spricht — dann wird NICHT aufgefrischt (auf Pause warten).
          this.userSpeaking = msg.state === "speaking";
          this.callbacks.onUserState?.(msg.state);
        }
      } else if (msg.type === "voice-v8.stt_reject") {
        this.callbacks.onSttReject?.({
          text: typeof msg.text === "string" ? msg.text : "",
          reason: typeof msg.reason === "string" ? msg.reason : "unknown",
          noSpeech: typeof msg.noSpeech === "number" ? msg.noSpeech : null,
          avgLogprob:
            typeof msg.avgLogprob === "number" ? msg.avgLogprob : null,
        });
      } else if (msg.type === "voice-v8.modules") {
        const list: ModulesList = {
          active: Array.isArray(msg.active) ? (msg.active as string[]) : [],
          inactive: Array.isArray(msg.inactive)
            ? (msg.inactive as Array<{ name: string; reason: string }>)
            : [],
          truthGuard: Boolean(msg.truthGuard),
        };
        try {
          localStorage.setItem(
            "voice-v8.modules",
            JSON.stringify({ ...list, receivedAt: Date.now() }),
          );
        } catch {
          /* localStorage unavailable */
        }
        this.callbacks.onModulesList?.(list);
      }
    } catch {
      // non-json data packet — ignore
    }
  }

  /** Gibt `(participant: RemoteParticipant) => …` ein wenn der Agent eigenen
   * Subscriber-Hook braucht — derzeit Stub. */
   
  _onAgentParticipant(_p: RemoteParticipant): void {}
}
